import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useEffect, type FC } from 'react';
import { MdForum } from 'react-icons/md';
import { useLocation, useSearch } from 'react-location';
import { createLogger } from '../../modules/prelude/logger';
import shl_content_image from './../../../assets/shl_content_image.png';
import { useCreateWebchat } from '../../modules/datastores/useCreateWebchat';
import AdditionalTerms from './AdditionalTerms';
import { LANGUAGE_ES, taskIdPattern } from '../../modules/prelude/constants';
import Cookies from 'universal-cookie';
import type { UseQueryResult } from 'react-query';
import type { HotlineMetaData } from '../../modules/algebra/global';

const fileLabel = 'TOSTerms.ts';
const logger = createLogger({
  fileLabel,
});

const TOSTerms: FC<{statusQuery:UseQueryResult<HotlineMetaData>}> = ({statusQuery}) => {
  const location = useLocation();
  const query = useSearch();
  const messaging_service_key = query['messaging_service_key'] as string;
  const { mutateAsync: createWebchat, isLoading } = useCreateWebchat();
  const cookies = new Cookies();
  const { endpointTitle, status, endpointLang} = statusQuery.data || {};
  const redirect = (createChat: string) => {
    location.history.replace(`/?messaging_service_key=${messaging_service_key}&task_sid=${createChat}`);
  };
  const startTalking = async () => {
    try {
      const createChat = await createWebchat({ messaging_service_key });
      const date = new Date();
      date.setTime(date.getTime() + 10 * 60 * 1000);
      cookies.set(`task_sid${messaging_service_key}`, createChat, { path: '/', expires: date });
      redirect(createChat);
    } catch (startChatError) {
      logger.error('Error While Starting Conversation', startChatError);
    }
  };

  useEffect(() => {
    if (taskIdPattern.test(cookies.get(`task_sid${messaging_service_key}`))) {
      redirect(cookies.get(`task_sid${messaging_service_key}`));
    }
  }, []);

  return (
    <div className="bg-pc-1 py-8">
      <div className="px-4 md:px-14">
        <Typography variant="h1" className="text-tc-4 flex justify-center text-2xl font-bold md:text-left text-center">
          {endpointTitle || (endpointLang === LANGUAGE_ES ? `La Línea de Ayuda Nacional Online de Abuso Sexual` : `The National Sexual Assault Hotline  Online Hotline`)}
        </Typography>
        <Typography variant="body1" className="text-xl italic text-center text-tc-1 border-b border-tc-4 pb-3">
          {endpointLang === LANGUAGE_ES
            ? `Hable con un especialista de apoyo quien puede proporcionar apoyo confidencial `
            : `Chat online with a trained staff member who can provide you confidential crisis support.`}
        </Typography>
      </div>
      <div className="flex flex-col lg:flex-row md:pl-8 sm:pl-0">
        <div className="w-full lg:w-1/2 px-4 md:px-14 py-4">
          <Typography className="text-lg text-tc-1">
            {endpointLang === LANGUAGE_ES
              ? `Su privacidad y seguridad son importantes. Asegúrese de estar en un lugar seguro y que esté usando un dispositivo y conexión de internet seguro. Tenga presente que aunque hemos tomado numerosas medidas para mantener segura su comunicación mientras utiliza nuestro sitio, ninguna transmisión por internet es 100% segura.`
              : `Your privacy and safety are crucial. Please make sure you are in a safe place and that you are using a secure device and Internet connection. Please note that while we have taken numerous
            measures to keep your communications safe while using our site, no Internet transmission is 100% secure.`}
          </Typography>

          {endpointLang === LANGUAGE_ES ? (
            <Link href="https://rainn.org/lo-que-hemos-hecho-para-que-su-seguridad" className="text-tc-4 uppercase font-bold py-3 inline-block underline">
              Obtenga más información
            </Link>
          ) : (
            <Link href="https://rainn.org/what-weve-done-make-it-safe" className="text-tc-4 uppercase font-bold py-3 inline-block underline">
              Learn More
            </Link>
          )}

          <Typography className="text-sm text-tc-1 pb-3">
            {endpointLang === LANGUAGE_ES ? 'Condiciones' : 'Terms'} :
            <br />
            {endpointLang === LANGUAGE_ES
              ? `Durante nuestra sesión no le pediremos información, como su nombre o dirección, que la(o) pudiera identificar y tampoco grabaremos la sesión. Sin embargo, si usted comparte con nosotros su nombre y ubicación, y es menor de los 18 años de edad; o si tememos que usted corre el peligro de terminar con su vida; o si de otra forma lo requiere la ley, quizás tengamos que proporcionar la
               información que usted nos haya dado a las autoridades apropiadas (como la policía).`
              : `During your session, we will not ask for information that would identify you (such as your name or address) and we will not record the chat. However, if you disclose your name and
            location and are under 18 or if we fear that you are in danger of committing suicide (or otherwise as required by law), we may have to provide information you have given us to the
            appropriate authorities.`}
            <br />
            {endpointLang === LANGUAGE_ES ? (
              <Link href="https://rainn.org/terminos-de-servicio" className="text-tc-4 uppercase font-bold py-3 inline-block underline">
                Lea aquí todas las condiciones
              </Link>
            ) : (
              <Link href="https://rainn.org/terms-service" className="text-tc-4 uppercase font-bold py-3 inline-block underline">
                READ THE FULL TERMS
              </Link>
            )}
          </Typography>
          <AdditionalTerms />
          {(() => {
            if (statusQuery.isLoading) return <CircularProgress />;
            else if (statusQuery.isSuccess && status)
              return (
                <>
                  <Button startIcon={<MdForum className="w-12 h-12" />} onClick={startTalking} className="chat-now" disabled={isLoading}>
                    {endpointLang === LANGUAGE_ES ? 'Chatear ahora' : 'Chat Now'}
                  </Button>
                  <Typography variant="h2" className="text-tc-1 font-bold pt-3 text-lg">
                    {endpointLang === LANGUAGE_ES ? 'Al hacer clic en "Chatear ahora", acepta los términos.' : 'By clicking "Chat Now," you are agreeing to the terms.'}
                  </Typography>
                </>
              );
            else
              return (
                <Typography variant="h2" className="text-tc-1 font-bold pt-3 text-lg">
                  {endpointLang === LANGUAGE_ES ? 'Actualmente todas nuestras operadoras están ocupadas.' : 'Currently All our Operators Are Busy'}
                </Typography>
              );
          })()}
        </div>
        <div className="w-full sm:w-1/2 px-4 md:px-14 py-4 mt-2">
          <div
            className="video"
            style={{
              position: 'relative',
              paddingBottom: '56.25%' /* 16:9 */,
              paddingTop: 25,
              height: 0,
            }}
          >
            <img
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              src={shl_content_image}
              alt="Some Content"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TOSTerms;
